/**
 * Public pack listing. Read-only catalogue of published packs — no cart,
 * checkout, order or inventory interaction (those arrive in Phase 4).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { PackageOpen } from "lucide-react";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackCollectionCover } from "@/lib/catalog/fallback-images";
import { publicPacksQuery } from "@/lib/packs/queries";
import { canonicalUrl } from "@/lib/seo/site";

export const Route = createFileRoute("/packs/")({
  loader: ({ context }) => {
    void context.queryClient.ensureQueryData(publicPacksQuery());
  },
  head: () => ({
    meta: [
      { title: "Fragrance Packs — Build Your CB16 Discovery Set" },
      {
        name: "description",
        content:
          "Compose your own CB16 fragrance pack. Choose your bottles one by one, watch your pack fill in real time, and see live pricing as you build.",
      },
      { property: "og:title", content: "Fragrance Packs — CB16 Maison" },
      {
        property: "og:description",
        content: "Build a bespoke CB16 pack — choose your bottles, see live pricing, and preview your selection.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl("/packs") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/packs") }],
  }),
  component: PacksPage,
  errorComponent: () => <Fallback title="Packs are unavailable" body="We could not load packs just now. Please try again shortly." />,
  notFoundComponent: () => <Fallback title="Not found" body="This page does not exist." />,
});

function PacksPage() {
  const { data } = useSuspenseQuery(publicPacksQuery());
  const packs = data.packs;

  return (
    <div className="bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <section className="border-b border-black/10 bg-[#faf7f2]">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
          <span className="text-[11px] font-medium uppercase tracking-[0.32em] text-[#C6A15B]">Compose</span>
          <h1
            className="mt-4 text-4xl font-normal leading-tight text-black sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Fragrance Packs
          </h1>
          <div className="mt-6 h-px w-10 bg-[#C6A15B]" />
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-black/60 sm:text-base">
            Choose a pack, then compose it bottle by bottle from the CB16 atelier. Live pricing, live
            progress, and a summary that updates as you build.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        {data.error && (
          <p className="mb-8 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{data.error}</p>
        )}

        {packs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((pack) => {
              const counts = pack.variants.map((v) => v.bottleCount);
              return (
                <Link
                  key={pack.id}
                  to="/packs/$slug"
                  params={{ slug: pack.slug }}
                  className="group flex flex-col overflow-hidden border border-black/10 bg-white transition-shadow hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]"
                >
                  <div className="aspect-4/3 overflow-hidden bg-[#faf7f2]">
                    <SafeImage
                      src={pack.thumbnailImage ?? pack.heroImage ?? pack.bannerImage}
                      fallback={fallbackCollectionCover(pack.slug)}
                      alt={pack.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-6">
                    <h2 className="text-xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {pack.name}
                    </h2>
                    <p className="text-sm leading-relaxed text-black/60">
                      {pack.shortDescription ?? `Compose your own ${pack.bottleSizeMl} ML pack.`}
                    </p>
                    <p className="mt-auto pt-3 text-[11px] uppercase tracking-[0.2em] text-black/45">
                      {counts.length > 0 ? `${counts.join(" or ")} bottles` : "Custom"} · {pack.bottleSizeMl} ML
                    </p>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">
                      Build this pack →
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center border border-black/10 bg-[#faf7f2] px-6 py-20 text-center">
      <PackageOpen className="mb-5 h-10 w-10 text-black/25" strokeWidth={1.25} aria-hidden="true" />
      <h2 className="text-2xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
        No packs available yet
      </h2>
      <p className="mx-auto mt-3 max-w-md text-sm text-black/60">
        Our atelier is composing new packs. In the meantime, explore the full CB16 fragrance library.
      </p>
      <Link
        to="/fragrances"
        className="mt-8 inline-block border border-black bg-black px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-white hover:bg-black/85"
      >
        Browse fragrances
      </Link>
    </div>
  );
}

function Fallback({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
      <h1 className="text-3xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>{title}</h1>
      <p className="mt-4 text-sm text-black/60">{body}</p>
      <Link to="/packs" className="mt-8 inline-block text-[11px] font-semibold uppercase tracking-[0.24em] text-[#C6A15B]">
        Back to packs
      </Link>
    </div>
  );
}